/* global URL_AGENT_CHAT */
import { FC, FormEvent, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import { ga4Event } from '@utils/analytics/ga4';
import paulinaAvatar from '@assets/images/paulina-avatar.jpg';

/**
 * Widget de chat con Paulina, la asistente de soporte/CS (agente IA).
 * Habla con el Worker de Cloudflare (plick-agent-pc): POST /api/chat para
 * enviar y GET /api/chat/:id por polling hasta obtener la respuesta.
 * El Worker valida la firma del JWT de sesión del panel (el mismo token de
 * localStorage que usan las APIs) — la identidad nunca viaja en el body.
 */

interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
}

// Convención de naming de eventos: `pau_<dominio>_<accion>` — prefijo pna
// (Paulina, sin doble sentido) para agruparlos alfabéticamente y encontrarlos en el search de
// Clarity/GA4; dominio + acción explícitos en pasado. Catálogo:
//   pau_chat_abierto · pau_chat_limpiado
//   pau_mensaje_enviado · pau_respuesta_ok · pau_respuesta_error
//   pau_navegacion_click
// track() los manda a GA4 (con params) y a Clarity (tag de sesión pau_usuario
// + evento sin params). Sin PII.
const track = (eventName: string, params: Record<string, string | number | boolean> = {}) => {
  try {
    ga4Event(eventName, params);
    const w = window as unknown as { clarity?: (...a: unknown[]) => void };
    if (typeof w.clarity === 'function') {
      w.clarity('set', 'pau_usuario', 'true');
      w.clarity('event', eventName);
    }
  } catch {
    /* la telemetría nunca rompe el chat */
  }
};

const POLL_INTERVAL_MS = 1000;
const POLL_TIMEOUT_MS = 90000;
const MAX_HISTORY = 20;
const STORAGE_KEY = 'agent_chat_history';

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const loadHistory = (): ChatTurn[] => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ChatTurn[]) : [];
  } catch {
    return [];
  }
};

const saveHistory = (turns: ChatTurn[]) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(turns.slice(-MAX_HISTORY)));
  } catch {
    /* storage lleno o bloqueado: el chat sigue funcionando sin persistencia */
  }
};

const IMAGE_URL = /^(?:captura:\s*)?(https?:\/\/\S+\.(?:jpe?g|png|webp))\s*$/i;
const NAV_LINE = /^ACCION_IR:(\/[\w/-]*)\|(.*)$/;
const SOPORTE_LINE = /^ACCION_SOPORTE:(https?:\/\/\S+)$/;
const MD_IMAGE = /!\[[^\]]*\]\(\s*(https?:\/\/[^)\s]+)\s*\)/g;
const MD_LINK = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;
const LINK = /(https?:\/\/[^\s)]+)/g;

const linkClass = 'break-all text-[#4298B5] underline';

interface Nudge {
  id: string;
  tipo: string;
  titulo: string;
  mensaje: string;
  cta:
    | { tipo: 'navegar'; path: string; seccion: string; label: string }
    | { tipo: 'escalar'; link: string; label: string }
    | { tipo: 'chat'; prompt: string; label: string };
}

// Cadencia: un nudge del mismo tipo no se repite en X horas (localStorage).
const NUDGE_COOLDOWN_H: Record<string, number> = {
  bienvenida: 9999,
  setup_incompleto: 24,
  diagnostico: 6,
  descubrimiento: 48,
  rageclick: 4,
};
const nudgeEnCooldown = (tipo: string): boolean => {
  try {
    const last = Number(localStorage.getItem(`pau_nudge_${tipo}`) || 0);
    const h = NUDGE_COOLDOWN_H[tipo] ?? 24;
    return Date.now() - last < h * 3600 * 1000;
  } catch {
    return false;
  }
};
const marcarNudge = (tipo: string) => {
  try {
    localStorage.setItem(`pau_nudge_${tipo}`, String(Date.now()));
  } catch {
    /* sin storage: se mostrará otra vez, aceptable */
  }
};

const renderLinks = (text: string, key: string) =>
  text.split(LINK).map((part, i) =>
    LINK.test(part) ? (
      <a key={`${key}-${i}`} href={part} target="_blank" rel="noreferrer" className={linkClass}>
        {part}
      </a>
    ) : (
      part
    ),
  );

// Texto inline: links markdown [texto](url), URLs sueltas y **negritas**.
const renderInline = (text: string, key: string) => {
  const parts = text.split(MD_LINK); // [texto, label, url, texto, ...]
  const out = [];
  for (let i = 0; i < parts.length; i += 3) {
    const plain = parts[i] ?? '';
    out.push(
      ...plain
        .split(/\*\*([^*]+)\*\*/g)
        .map((p, j) =>
          j % 2 === 1 ? <strong key={`${key}-b${i}-${j}`}>{p}</strong> : renderLinks(p, `${key}-${i}-${j}`),
        ),
    );
    if (i + 2 < parts.length) {
      out.push(
        <a key={`${key}-l${i}`} href={parts[i + 2]} target="_blank" rel="noreferrer" className={linkClass}>
          {parts[i + 1]}
        </a>,
      );
    }
  }
  return out;
};

// Render ligero del mensaje: imágenes, links, negritas y botón de navegación.
const MessageContent: FC<{ text: string; onNavigate: (path: string, seccion: string) => void }> = ({
  text,
  onNavigate,
}) => {
  // Las imágenes markdown ![alt](url) se separan a su propia línea como URL.
  const normalized = text.replace(MD_IMAGE, '\n$1\n');
  return (
    <>
      {normalized.split('\n').map((line, i) => {
        const sop = line.trim().match(SOPORTE_LINE);
        if (sop) {
          return (
            <a
              key={i}
              href={sop[1]}
              target="_blank"
              rel="noreferrer"
              className="my-1 block w-full rounded-lg bg-[#25D366] px-3 py-2 text-center text-sm font-semibold text-[#ffffff]"
            >
              💬 Hablar con soporte →
            </a>
          );
        }
        const nav = line.trim().match(NAV_LINE);
        if (nav) {
          const seccion = nav[2] || 'la sección';
          return (
            <button
              key={i}
              type="button"
              onClick={() => onNavigate(nav[1], seccion)}
              className="my-1 w-full rounded-lg bg-[#4298B5] px-3 py-2 text-left text-sm font-semibold text-[#ffffff]"
            >
              Llévame a {seccion.replace(/_/g, ' ')} →
            </button>
          );
        }
        const img = line.trim().match(IMAGE_URL);
        if (img) {
          return (
            <img
              key={i}
              src={img[1]}
              alt="Captura del panel"
              loading="lazy"
              className="my-1 w-full rounded-lg border border-[#e2e8f0]"
            />
          );
        }
        return (
          <p key={i} className="my-0.5 min-h-[0.5rem] whitespace-pre-wrap break-words">
            {renderInline(line, `${i}`)}
          </p>
        );
      })}
    </>
  );
};

export const AgentChatWidget: FC = () => {
  const { isAuthenticated, isDeliveryPerson, user, commerceName } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<ChatTurn[]>(loadHistory);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [nudge, setNudge] = useState<Nudge | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [open, turns, busy]);

  // Rol de activación: pide nudges proactivos al Worker (1 vez por sesión).
  useEffect(() => {
    if (!isAuthenticated || isDeliveryPerson) return;
    if (typeof URL_AGENT_CHAT === 'undefined' || !URL_AGENT_CHAT) return;
    if (sessionStorage.getItem('pau_hints_done')) return;
    sessionStorage.setItem('pau_hints_done', '1');
    const token = localStorage.getItem('token');
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`${URL_AGENT_CHAT}/api/hints`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const { nudges } = (await res.json()) as { nudges: Nudge[] };
        const elegible = (nudges || []).find((n) => !nudgeEnCooldown(n.tipo));
        if (elegible) {
          setNudge(elegible);
          track('pau_proactivo_mostrado', { tipo: elegible.tipo, id: elegible.id });
        }
      } catch {
        /* sin nudges: experiencia normal */
      }
    })();
  }, [isAuthenticated, isDeliveryPerson]);

  // Rage click: 4+ clics rápidos en el mismo punto → ofrecer ayuda.
  useEffect(() => {
    let count = 0;
    let last = 0;
    let lx = 0;
    let ly = 0;
    const onClick = (e: MouseEvent) => {
      const now = Date.now();
      const cerca = Math.abs(e.clientX - lx) < 40 && Math.abs(e.clientY - ly) < 40;
      count = now - last < 600 && cerca ? count + 1 : 1;
      last = now;
      lx = e.clientX;
      ly = e.clientY;
      if (count >= 4 && !nudgeEnCooldown('rageclick')) {
        count = 0;
        track('pau_rage_click', { page: window.location.pathname });
        setNudge({
          id: 'rageclick',
          tipo: 'rageclick',
          titulo: '¿Te atoraste? 🤔',
          mensaje: 'Vi que le estás dando varias veces sin que pase nada. ¿Te echo la mano con lo que intentas hacer?',
          cta: {
            tipo: 'chat',
            prompt: 'Estoy batallando con algo en esta pantalla, ¿me ayudas?',
            label: 'Sí, ayúdame →',
          },
        });
      }
    };
    window.addEventListener('click', onClick);
    return () => window.removeEventListener('click', onClick);
  }, []);

  if (!isAuthenticated || isDeliveryPerson || typeof URL_AGENT_CHAT === 'undefined' || !URL_AGENT_CHAT) {
    return null;
  }

  const doSend = async (message: string) => {
    if (!message || busy) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    const history = turns.slice(-MAX_HISTORY);
    const nextTurns: ChatTurn[] = [...turns, { role: 'user', content: message }];
    setTurns(nextTurns);
    setInput('');
    setBusy(true);
    setError('');
    track('pau_mensaje_enviado', { page: location.pathname, chars: message.length });
    const startedAt = Date.now();

    try {
      const res = await fetch(`${URL_AGENT_CHAT}/api/chat`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          history,
          // Defensa: user.name puede no venir (campo opcional del login);
          // fallback al nombre del comercio para no saludar con un nombre vacío.
          userName: user?.name || commerceName || undefined,
          // Contexto situacional: Paulina sabe en qué sección está el usuario.
          page: location.pathname,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { chatId } = (await res.json()) as { chatId: string };

      const deadline = Date.now() + POLL_TIMEOUT_MS;
      let reply = '';
      while (Date.now() < deadline) {
        await sleep(POLL_INTERVAL_MS);
        const poll = await fetch(`${URL_AGENT_CHAT}/api/chat/${chatId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!poll.ok) throw new Error(`HTTP ${poll.status}`);
        const data = (await poll.json()) as { status: string; reply?: string };
        if (data.status === 'complete') {
          reply = data.reply ?? '';
          break;
        }
        if (data.status === 'error') throw new Error('workflow error');
      }
      if (!reply) throw new Error('timeout');

      const finalTurns: ChatTurn[] = [...nextTurns, { role: 'assistant', content: reply }];
      setTurns(finalTurns);
      saveHistory(finalTurns);
      track('pau_respuesta_ok', {
        ms: Date.now() - startedAt,
        con_captura: /Captura:\s*http/.test(reply),
        con_navegacion: /ACCION_IR:/.test(reply),
      });
    } catch (err) {
      setError('No pude obtener respuesta. Intenta de nuevo en un momento.');
      setTurns(turns);
      track('pau_respuesta_error', { motivo: err instanceof Error ? err.message.slice(0, 40) : 'desconocido' });
    } finally {
      setBusy(false);
    }
  };

  const goTo = (path: string, seccion: string) => {
    track('pau_navegacion_click', { seccion, path });
    setOpen(false);
    navigate(path);
  };

  const aceptarNudge = (n: Nudge) => {
    track('pau_proactivo_aceptado', { tipo: n.tipo, id: n.id });
    marcarNudge(n.tipo);
    setNudge(null);
    if (n.cta.tipo === 'navegar') navigate(n.cta.path);
    else if (n.cta.tipo === 'escalar') window.open(n.cta.link, '_blank');
    else {
      setOpen(true);
      doSend(n.cta.prompt);
    }
  };
  const descartarNudge = (n: Nudge) => {
    track('pau_proactivo_descartado', { tipo: n.tipo, id: n.id });
    marcarNudge(n.tipo);
    setNudge(null);
  };

  const clearConversation = () => {
    track('pau_chat_limpiado');
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* sin storage no hay nada que borrar */
    }
    setTurns([]);
    setError('');
  };

  const send = (e: FormEvent) => {
    e.preventDefault();
    doSend(input.trim());
  };

  return (
    <>
      {open && (
        <div className="fixed bottom-36 right-4 z-[950] flex h-[65vh] max-h-[540px] w-[92vw] max-w-sm flex-col overflow-hidden rounded-xl border border-[#e2e8f0] bg-[#ffffff] text-[#253746] shadow-2xl md:right-6">
          <div className="flex items-center justify-between bg-[#253746] px-4 py-3 text-[#ffffff]">
            <div className="flex items-center gap-3">
              <img
                src={paulinaAvatar}
                alt="Paulina"
                className="h-9 w-9 rounded-full border-2 border-[#4298B5] object-cover"
              />
              <div>
                <p className="font-semibold leading-tight">Paulina</p>
                <p className="text-xs opacity-80">Asistente de Plick</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                aria-label="Borrar conversación"
                title="Borrar conversación"
                className="text-sm opacity-80 hover:opacity-100 disabled:opacity-40"
                disabled={busy || turns.length === 0}
                onClick={clearConversation}
              >
                🗑
              </button>
              <button
                type="button"
                aria-label="Cerrar chat"
                className="text-2xl leading-none"
                onClick={() => setOpen(false)}
              >
                ×
              </button>
            </div>
          </div>

          <div className="flex-1 space-y-2 overflow-y-auto p-3 text-sm">
            {turns.length === 0 && (
              <p className="mt-4 text-center text-[#64748b]">
                ¡Hola! Soy Paulina 👋 Pregúntame cómo usar Plick o sobre tu operación.
              </p>
            )}
            {turns.map((t, i) => (
              <div
                key={i}
                className={
                  t.role === 'user'
                    ? 'ml-8 rounded-lg rounded-br-none bg-[#253746] px-3 py-2 text-[#ffffff]'
                    : 'mr-8 rounded-lg rounded-bl-none bg-[#f1f4f6] px-3 py-2 text-[#253746]'
                }
              >
                <MessageContent text={t.content} onNavigate={goTo} />
              </div>
            ))}
            {busy && (
              <div className="mr-8 rounded-lg bg-[#f1f4f6] px-3 py-2 text-[#253746]">
                <span className="animate-pulse">Paulina está escribiendo…</span>
              </div>
            )}
            {error && <p className="text-xs text-[#dc2626]">{error}</p>}
            <div ref={bottomRef} />
          </div>

          <form onSubmit={send} className="flex gap-2 border-t border-[#e2e8f0] p-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Escribe tu pregunta…"
              className="flex-1 rounded-lg border border-[#e2e8f0] bg-[#ffffff] px-3 py-2 text-sm text-[#253746] outline-none"
              disabled={busy}
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              className="rounded-lg bg-[#253746] px-4 py-2 text-sm font-semibold text-[#ffffff] disabled:opacity-50"
            >
              Enviar
            </button>
          </form>
        </div>
      )}

      {/* Rol de activación: tarjeta proactiva descartable (no abre el chat sola). */}
      {!open && nudge && (
        <div className="fixed bottom-36 right-4 z-[949] w-[88vw] max-w-xs rounded-xl border border-[#e2e8f0] bg-[#ffffff] p-4 shadow-2xl md:right-6">
          <div className="mb-1 flex items-start justify-between gap-2">
            <p className="font-semibold text-[#253746]">{nudge.titulo}</p>
            <button
              type="button"
              aria-label="Descartar"
              className="text-lg leading-none text-[#94a3b8]"
              onClick={() => descartarNudge(nudge)}
            >
              ×
            </button>
          </div>
          <p className="mb-3 text-sm text-[#475569]">{nudge.mensaje}</p>
          <button
            type="button"
            onClick={() => aceptarNudge(nudge)}
            className="w-full rounded-lg bg-[#4298B5] px-3 py-2 text-sm font-semibold text-[#ffffff]"
          >
            {nudge.cta.label}
          </button>
        </div>
      )}

      <button
        type="button"
        aria-label="Abrir chat de ayuda"
        onClick={() => {
          if (!open) track('pau_chat_abierto', { page: location.pathname });
          setOpen((v) => !v);
        }}
        className="fixed bottom-20 right-4 z-[950] flex h-14 w-14 items-center justify-center overflow-hidden rounded-full bg-[#253746] text-2xl text-[#ffffff] shadow-2xl transition-transform duration-300 hover:scale-105 md:right-6"
      >
        {open ? '×' : <img src={paulinaAvatar} alt="Abrir chat con Paulina" className="h-full w-full object-cover" />}
      </button>
    </>
  );
};
